import { useEffect, useRef, useState } from 'react';

// Must match --sheet-out in src/styles/design.css.
const EXIT_MS = 220;

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Keeps a sheet mounted long enough to play its exit animation.
 *
 * A component that does `if (!open) return null` rips itself out of the DOM the
 * instant open flips, so it can only ever animate in — the close is a hard cut.
 * This holds the mount for one exit duration instead.
 *
 *   const { mounted, closing } = useSheetAnim(open);
 *   if (!mounted) return null;
 *   <div className={`sheet-scrim${closing ? ' is-closing' : ''}`}>
 *     <div className={`sheet-panel${closing ? ' is-closing' : ''}`}>…</div>
 *   </div>
 *
 * Collapses to an instant unmount when the reader asks for reduced motion.
 */
export default function useSheetAnim(open) {
  // closed → nothing rendered · open → on screen · closing → playing its exit
  const [state, setState] = useState(open ? 'open' : 'closed');
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);

    if (open) {
      setState('open');
      return undefined;
    }

    // Already closed (e.g. first render) — don't schedule a pointless exit.
    setState((s) => (s === 'closed' ? 'closed' : 'closing'));
    timer.current = setTimeout(() => setState('closed'), prefersReducedMotion() ? 0 : EXIT_MS);
    return () => clearTimeout(timer.current);
  }, [open]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { mounted: state !== 'closed', closing: state === 'closing' };
}
